import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getJobListings, submitJobApplication } from '../../../services/api';

const Careers = () => {
  const [jobListings, setJobListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationDialog, setApplicationDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [application, setApplication] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cover_letter: null,
    resume: null,
    id_document: null,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [jobDetailsDialog, setJobDetailsDialog] = useState(false);

  useEffect(() => {
    fetchJobListings();
  }, []);

  const fetchJobListings = async () => {
    try {
      const response = await getJobListings();
      setJobListings(response.data.results);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch job listings');
      setLoading(false);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setJobDetailsDialog(true);
  };

  const handleCloseJobDetails = () => {
    setJobDetailsDialog(false);
    setSelectedJob(null);
  };

  const handleProceedToApplication = () => {
    setJobDetailsDialog(false);
    setApplicationDialog(true);
  };

  const handleCloseDialog = () => {
    setApplicationDialog(false);
    setSelectedJob(null);
    setActiveStep(0);
    setApplication({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      cover_letter: null,
      resume: null,
      id_document: null,
    });
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFileChange = (e, field) => {
    setApplication({ ...application, [field]: e.target.files[0] });
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('job', selectedJob.id);
      formData.append('first_name', application.first_name);
      formData.append('last_name', application.last_name);
      formData.append('email', application.email);
      formData.append('phone', application.phone);
      formData.append('cover_letter', application.cover_letter);
      formData.append('resume', application.resume);
      formData.append('id_document', application.id_document);

      await submitJobApplication(formData);
      setSnackbar({
        open: true,
        message: 'Application submitted successfully!',
        severity: 'success',
      });
      handleCloseDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to submit application. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const steps = ['Personal Information', 'Documents Upload', 'Review & Submit'];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={application.first_name}
                onChange={(e) => setApplication({ ...application, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={application.last_name}
                onChange={(e) => setApplication({ ...application, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={application.email}
                onChange={(e) => setApplication({ ...application, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={application.phone}
                onChange={(e) => setApplication({ ...application, phone: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Cover Letter
              </Typography>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileChange(e, 'cover_letter')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Resume
              </Typography>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileChange(e, 'resume')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                ID Document
              </Typography>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, 'id_document')}
                required
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Application Summary
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Personal Information
              </Typography>
              <Typography>
                Name: {application.first_name} {application.last_name}
              </Typography>
              <Typography>Email: {application.email}</Typography>
              <Typography>Phone: {application.phone}</Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Documents
              </Typography>
              <Typography>Cover Letter: {application.cover_letter?.name}</Typography>
              <Typography>Resume: {application.resume?.name}</Typography>
              <Typography>ID Document: {application.id_document?.name}</Typography>
            </Paper>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderHtmlContent = (html) => {
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const truncateHtml = (html, maxLength = 150) => {
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText;
    
    if (text.length <= maxLength) return html;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <Box>
      {/* Custom Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '160px', md: '180px' },
          paddingBottom: '60px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/good1.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000',
            opacity: 0.7,
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              color="white"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Join Our Team
            </Typography>
            <Typography
              variant="h5"
              color="white"
              sx={{
                maxWidth: '800px',
                opacity: 0.9,
              }}
            >
              Build your career with World's leading geospatial technology company
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Job Listings Section */}
      <Box sx={{ py: 6, backgroundColor: '#f5f5f5' }}>
        <Container>
          {jobListings.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2,
                backgroundColor: 'white',
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No Job Listings Available
              </Typography>
              <Typography variant="body1" color="text.secondary">
                We currently don't have any open positions. Please check back later or subscribe to our newsletter to be notified when new opportunities become available.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {jobListings.map((job, index) => (
                <Grid item xs={12} md={6} key={job.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                  >
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom>
                          {job.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Chip
                            icon={<LocationOnIcon />}
                            label={job.location}
                            color="primary"
                            size="small"
                          />
                          <Chip
                            icon={<AccessTimeIcon />}
                            label={job.type}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                          {job.department}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {renderHtmlContent(truncateHtml(job.description))}
                        </Box>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleJobClick(job)}
                        >
                          Apply Now
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Job Details Dialog */}
      <Dialog
        open={jobDetailsDialog}
        onClose={handleCloseJobDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedJob?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                icon={<LocationOnIcon />}
                label={selectedJob?.location}
                color="primary"
              />
              <Chip
                icon={<AccessTimeIcon />}
                label={selectedJob?.type}
                variant="outlined"
              />
            </Box>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Department: {selectedJob?.department}
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom>
            Job Description
          </Typography>
          <Box sx={{ mb: 3 }}>
            {renderHtmlContent(selectedJob?.description)}
          </Box>

          <Typography variant="h6" gutterBottom>
            Requirements
          </Typography>
          <Box sx={{ mb: 3 }}>
            {renderHtmlContent(selectedJob?.requirements)}
          </Box>

          <Typography variant="h6" gutterBottom>
            Key Responsibilities
          </Typography>
          <Box sx={{ mb: 3 }}>
            {renderHtmlContent(selectedJob?.key_responsibilities)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJobDetails}>Close</Button>
          <Button onClick={handleProceedToApplication} variant="contained" color="primary">
            Proceed to Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* Application Dialog */}
      <Dialog
        open={applicationDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Apply for {selectedJob?.title}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent(activeStep)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit Application
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Careers; 